import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from "@angular/core";
import {StatusPanelService} from "../../content-layout/status-panel/status-panel.service";
import {ErrorHandlerService} from "../../../error-handler.service";
import {FUIAlertAction, FUIAlertActions, FUIAlertService, FUISpinnersService} from "@fedelta-pos/ui";
import {VenuePlanService} from "./venue-plan.service";
import {ContractPricingTier, VenuePlanData} from "./venue-plan.data";
import {FormGroup} from "@angular/forms";
import {CustomerInstanceVenueModel} from "../customer-instance-venue.model";
import {UserData} from "../../auth/user.data";
import {Subscription} from "rxjs";

@Component({
    selector: "fhm-venue-plan",
    styles: [":host {display: flex; width: 100%;}"],
    templateUrl: "./venue-plan.component.html",
    styleUrls: ["./venue-plan.component.scss"],
    providers: [VenuePlanService],
    standalone: false
})
export class VenuePlanComponent implements OnInit, OnDestroy {

    UserData = UserData;

    editing: boolean;

    data: VenuePlanData;

    initialTermStartDate: Date;

    useTieredPricing: boolean;

    cancelling: boolean;

    changingCancellationReason: boolean;

    private subscriptions: Subscription = new Subscription();

    @Input() formGroupPlan: FormGroup;
    @Input() loggedUser: UserData;

    constructor(private statusPanelService: StatusPanelService,
                private spinnersService: FUISpinnersService,
                public model: CustomerInstanceVenueModel,
                private errorHandler: ErrorHandlerService,
                private changeDetectorRef: ChangeDetectorRef,
                private planService: VenuePlanService,
                private alertService: FUIAlertService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(this.model.data$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
            this.data = this.model.draft ? this.model.data.venuePlan : Object.assign({}, this.model.data.venuePlan);
            this.editing = this.model.draft || this.newEntity;
            this.initialTermStartDate = undefined;
            if (this.data.initialTermStartDate) {
                this.initialTermStartDate = new Date(this.data.initialTermStartDate);
            }

            this.useTieredPricing = this.data.pricingTiers && this.data.pricingTiers.length > 0;
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get custom(): boolean {
        return this.data && this.data.planName && this.data.planName.indexOf("CS") !== -1;
    }

    get tyro(): boolean {
        return this.data && this.data.planName && this.data.planName.indexOf("TYRO") !== -1;
    }

    async save(): Promise<void> {
        if (this.data.planName) {
            try {
                await this.model.updatePlan(this.data);
                this.editing = false;
            } catch (e) {
                this.errorHandler.handleErrorResponse(e);
            }
        }
    }

    edit(): void {
        this.editing = true;
    }

    cancelEdit(): void {
        this.data = Object.assign({}, this.model.data.venuePlan);
        this.editing = false;
    }

    get newEntity(): boolean {
        return false;
    }

    async cancelCurrentSubscription(): Promise<void> {
        this.cancelling = false;
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.venue-plan.alert.cancel.title",
            "fhm.venue-plan.alert.cancel.message").toPromise();

        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.planService.cancelCurrentSubscription(this.model.venueId, this.model.data.subscription.cancellationReason);
                await this.model.refresh();
            } finally {
                this.spinnersService.close();
            }
        }
    }

    async cancelChangeCancellationReason(): Promise<void> {
        this.changingCancellationReason = false;
        this.spinnersService.open();
        try {
            await this.model.refresh();
        } finally {
            this.spinnersService.close();
        }
    }

    async changeCancellationReason(): Promise<void> {
        this.changingCancellationReason = false;
        this.spinnersService.open();
        try {
            await this.planService.changeCancellationReason(this.model.venueId, this.model.data.subscription.cancellationReason);
            await this.model.refresh();
        } finally {
            this.spinnersService.close();
        }
    }

    async resumeCurrentSubscription(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.venue-plan.alert.resume.title",
            "fhm.venue-plan.alert.resume.message").toPromise();

        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.planService.resumeCurrentSubscription(this.model.venueId);
                await this.model.refresh();
            } finally {
                this.spinnersService.close();
            }
        }
    }

    async churnCurrentSubscription(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.venue-plan.alert.churn.title",
            "fhm.venue-plan.alert.churn.message").toPromise();

        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.planService.churnCurrentSubscription(this.model.venueId);
                await this.model.refresh();
            } finally {
                this.spinnersService.close();
            }
        }
    }

    changeInitialTermDate(value: Date): void {
        this.initialTermStartDate = value;
        if (value) {
            this.data.initialTermStartDate = this.dateToString(value);
        } else {
            this.data.initialTermStartDate = undefined;
        }
    }

    changeUseTieredPricing(value: boolean): void {
        this.useTieredPricing = value;
        if (!value) {
            this.data.pricingTiers = [];
            this.data.currentTier = 0;
            this.data.lockTierUntilDate = undefined;
        }
    }

    changePlanSelection(): void {
        if (this.editing && this.tyro) {
            if (!this.data.stripeDomesticFeePercentage) {
                this.data.stripeDomesticFeePercentage = 1.75;
            }

            if (!this.data.stripeDomesticFeeFixed) {
                this.data.stripeDomesticFeeFixed = 0.30;
            }
        }

        if (this.editing && this.custom && !this.useTieredPricing) {
            this.useTieredPricing = true;
            this.data.pricingTiers = [];

            let tier: ContractPricingTier = new ContractPricingTier();
            tier.tierMonthlyAmount = 0;
            tier.feePercentage = 2.5;
            this.data.pricingTiers.push(tier);

            tier = new ContractPricingTier();
            tier.tierMonthlyAmount = 28000.00;
            tier.feePercentage = 2.25;
            this.data.pricingTiers.push(tier);

            tier = new ContractPricingTier();
            tier.tierMonthlyAmount = 52000.00;
            tier.feePercentage = 1.9;
            this.data.pricingTiers.push(tier);

            tier = new ContractPricingTier();
            tier.tierMonthlyAmount = 100000.00;
            tier.feePercentage = 1.6;
            this.data.pricingTiers.push(tier);

            this.data.currentTier = 1;
        }

    }

    dateToString(value: Date): string {
        const month: number = value.getMonth() + 1;
        return value.getFullYear() + "-" + (month < 10 ? "0" : "") + month + "-"
            + (value.getDate() < 10 ? "0" : "") + value.getDate();
    }

    addTier(): void {
        const tier: ContractPricingTier = new ContractPricingTier();
        if (this.data.pricingTiers.length === 0) {
            tier.tierMonthlyAmount = 0;
        }
        this.data.pricingTiers.push(tier);
        if (!this.data.currentTier || this.data.currentTier === 0) {
            this.data.currentTier = 1;
        }
    }

    removeTier(): void {
        this.data.pricingTiers.splice(this.data.pricingTiers.length - 1, 1);
    }


    tierLabel(idx: number): string {

        const fromAmount = this.data.pricingTiers[idx].tierMonthlyAmount;
        if (!fromAmount && idx !== 0) {
            return "";
        }

        // Check that there are no gaps
        if (idx > 0) {
            for (let i = 1; i < idx; i++) {
                if (!this.data.pricingTiers[i].tierMonthlyAmount) {
                    return "";
                }
            }
        }

        if (idx === this.data.pricingTiers.length - 1) {
            return " ($" + Intl.NumberFormat().format(fromAmount) + " and over) per month";
        }

        const toAmount = this.data.pricingTiers[idx + 1].tierMonthlyAmount;

        if (!toAmount) {
            return " ($" + Intl.NumberFormat().format(fromAmount) + " and over) per month";
        }
        return " ($" + Intl.NumberFormat().format(fromAmount) + " to " + Intl.NumberFormat().format(toAmount - 0.01) + ") per month";
    }
}
